// VarnasaaraPrototypes.js - Demo page showcasing all visualization prototypes

import React, { useState } from 'react';
import { Container, Row, Col, Nav, Tab, Alert, Card } from 'react-bootstrap';
import VarnasaaraOption2 from './VarnasaaraOption2';
import VarnasaaraOption3 from './VarnasaaraOption3';
import VarnasaaraOption5 from './VarnasaaraOption5';
import vaishnavimData from '../vaishnavim.json';

const VarnasaaraPrototypes = () => {
  const [activeTab, setActiveTab] = useState('option2');

  const prototypes = [
    {
      key: 'option2',
      title: 'Option 2: Accordion Layout',
      description: 'Interactive accordion with collapsible sections for each component',
      component: VarnasaaraOption2,
      pros: ['Clear visual separation', 'Interactive expand/collapse', 'Metadata badges', 'Mobile responsive'],
      cons: ['Takes more vertical space', 'Requires user interaction']
    },
    {
      key: 'option3',
      title: 'Option 3: Color-Coded Brackets',
      description: 'Bracket-based layout with semantic color coding',
      component: VarnasaaraOption3,
      pros: ['Familiar bracket syntax', 'Color semantic meaning', 'Compact display', 'Print friendly'],
      cons: ['Less visual separation', 'Can get dense', 'Color dependency']
    },
    {
      key: 'option5',
      title: 'Option 5: Nested Hierarchy',
      description: 'Tree-like structure with icons and indentation',
      component: VarnasaaraOption5,
      pros: ['Clear hierarchy', 'Visual icons', 'Natural reading flow', 'Balanced layout'],
      cons: ['Icon dependency', 'Indentation management', 'Moderate complexity']
    }
  ];

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="mb-4">
            <Card.Header>
              <h2 className="mb-0">Varnasaara Visualization Prototypes</h2>
              <p className="mb-0 text-muted">
                Interactive prototypes for Issue #57 - Visual representation schemes for Varnasaara Varna Krama
              </p>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <strong>Sample Data:</strong> Using vaishnavim.json (वैष्णवीम्) for demonstration.
                Each prototype shows different approaches to visualizing the complex phonological data.
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
            <Nav variant="tabs" className="mb-4">
              {prototypes.map(prototype => (
                <Nav.Item key={prototype.key}>
                  <Nav.Link eventKey={prototype.key}>
                    {prototype.title}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            <Tab.Content>
              {prototypes.map(prototype => {
                const PrototypeComponent = prototype.component;
                return (
                  <Tab.Pane key={prototype.key} eventKey={prototype.key}>
                    <Row>
                      <Col xs={12}>
                        <Card>
                          <Card.Body>
                            <PrototypeComponent data={vaishnavimData} />
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                    
                    <Row className="mt-4">
                      <Col lg={6}>
                        <Card>
                          <Card.Header>
                            <h5>{prototype.title}</h5>
                          </Card.Header>
                          <Card.Body>
                            <p className="text-muted">{prototype.description}</p>
                            
                            <h6 className="text-success">Pros:</h6>
                            <ul className="list-unstyled">
                              {prototype.pros.map((pro, index) => (
                                <li key={index} className="mb-1">
                                  <span className="text-success">✓</span> {pro}
                                </li>
                              ))}
                            </ul>
                            
                            <h6 className="text-warning">Cons:</h6>
                            <ul className="list-unstyled">
                              {prototype.cons.map((con, index) => (
                                <li key={index} className="mb-1">
                                  <span className="text-warning">⚠</span> {con}
                                </li>
                              ))}
                            </ul>
                          </Card.Body>
                        </Card>
                      </Col>
                      
                      <Col lg={6}>
                        <Card>
                          <Card.Header>
                            <h6>Implementation Notes</h6>
                          </Card.Header>
                          <Card.Body>
                            <small className="text-muted">
                              {prototype.key === 'option2' && (
                                <>Uses React Bootstrap Accordion with custom styling. 
                                Components are collapsible by default with metadata badges.</>
                              )}
                              {prototype.key === 'option3' && (
                                <>Implements semantic color coding: Blue for consonants, 
                                Green for vowels, Gold for svara. Maintains readability without colors.</>
                              )}
                              {prototype.key === 'option5' && (
                                <>Uses Unicode icons and CSS indentation to create hierarchy. 
                                Components are visually grouped with consistent spacing.</>
                              )}
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Tab.Pane>
                );
              })}
            </Tab.Content>
          </Tab.Container>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>Technical Implementation</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h6>Tech Stack Used:</h6>
                  <ul>
                    <li>React 18.2.0</li>
                    <li>React Bootstrap 2.10.2</li>
                    <li>Bootstrap 5.3.3</li>
                    <li>Custom CSS with mobile responsiveness</li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>Key Features:</h6>
                  <ul>
                    <li>Structured data parsing from varnasaara field</li>
                    <li>Component type detection (consonant/vowel)</li>
                    <li>Metadata extraction (maatra, anga, svara)</li>
                    <li>Mobile-responsive design</li>
                  </ul>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VarnasaaraPrototypes;
